import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CardActions,
  CardContent,
  Chip,
  Tooltip,
  Typography,
} from '@mui/material';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { Box } from '@mui/system';
import DataCollection, { DataCollectionData } from 'benchmarking-domain/data-collection';
import { DataPointValue } from 'benchmarking-domain/data-point';
import { DynamicQuestionnaire } from 'benchmarking-domain/dynamic-questionnaire';
import Project from 'benchmarking-domain/project';
import ProjectSection from 'benchmarking-domain/project-section';
import { QuestionnaireSection } from 'benchmarking-domain/questionnaire';
import { deepEqual } from 'fast-equals';
import { useUsersEmailToNameMap } from 'hooks/use-users-email-to-name-map';
import { enqueueSnackbar } from 'notistack';

import UseApi from '../../../hooks/use-api';
import { useDebugMode } from '../../../hooks/use-debug-mode';
import { DynamicQuestionnaireAccordion } from './dynamic-questionnaire-accordion';
import { QuestionnaireSectionDetails } from './questionnaire-section-details';
import { QuestionnaireSectionTable } from './questionnaire-section-table';

const QuestionnaireSectionTableMemo = React.memo(QuestionnaireSectionTable, (a, b) => {
  return (
    deepEqual(a.dataCollection, b.dataCollection) &&
    a.valueIdPrefix === b.valueIdPrefix &&
    deepEqual(a.globalVariables, b.globalVariables) &&
    deepEqual(a.rows, b.rows)
  );
});

const QuestionnaireSectionDetailsMemo = React.memo(QuestionnaireSectionDetails, (a, b) => {
  return (
    deepEqual(a.dynamicQuestionnaireDetails, b.dynamicQuestionnaireDetails) &&
    deepEqual(a.project, b.project) &&
    deepEqual(a.projectSection, b.projectSection) &&
    deepEqual(a.questionnaireSection, b.questionnaireSection)
  );
});

const DynamicQuestionnaireAccordionMemo = React.memo(DynamicQuestionnaireAccordion, (a, b) => {
  return (
    a.expanded === b.expanded &&
    deepEqual(a.dataCollection, b.dataCollection) &&
    deepEqual(a.projectSection, b.projectSection) &&
    deepEqual(a.globalVariables, b.globalVariables) &&
    deepEqual(a.rows, b.rows) &&
    a.name === b.name &&
    a.itemTypeName === b.itemTypeName
  );
});

export interface QuestionnaireSectionFormProps {
  organisationId: string;
  projectId: string;
  questionnaireSection: QuestionnaireSection;
  dynamicQuestionnaires?: DynamicQuestionnaire[];
  numberOfInputsProvided: number;
  totalNumberOfInputs: number;
  globalVariables: DataCollectionData;
  project: Project;
  projectSections: ProjectSection[] | undefined;
}

export function QuestionnaireSectionAccordion({
  organisationId,
  projectId,
  questionnaireSection,
  dynamicQuestionnaires,
  numberOfInputsProvided,
  totalNumberOfInputs,
  globalVariables,
  project,
  projectSections,
}: QuestionnaireSectionFormProps): React.JSX.Element {
  const [isLoadingTriggered, setIsLoadingTriggered] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isDataCollectionDirty, setIsDataCollectionDirty] = useState<boolean>(false);
  const [isProjectSectionDirty, setIsProjectSectionDirty] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [dataCollection, setDataCollection] = useState<DataCollection>();
  const [prevDataCollection, setPrevDataCollection] = useState<DataCollection>();
  const [currentData, setCurrentData] = useState<DataCollectionData>();
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);

  const projectSection = useMemo(
    () => projectSections?.find((ps) => ps.id === questionnaireSection.id),
    [projectSections, questionnaireSection.id]
  );
  const [currentProjectSectionData, setCurrentProjectSectionData] = useState<ProjectSection | undefined>(undefined);

  const isDebugMode = useDebugMode();

  const { subscribeDataCollection, saveDataCollection, saveProjectSection } = UseApi();

  const sectionGlobalVariables = useMemo(() => {
    return {
      ...globalVariables,
      sectionid: questionnaireSection.id,
    };
  }, [globalVariables, questionnaireSection]);

  useEffect(() => {
    if (prevDataCollection !== dataCollection) {
      if (isDataCollectionDirty) {
        enqueueSnackbar(`"${questionnaireSection.name}" input data changed in the background!`, { variant: 'warning' });
      }

      // TODO: Handle changing dataCollection in the background by merge or something - not just override!
      setIsDataCollectionDirty(false);
      setCurrentData(dataCollection?.data ?? {});
      setPrevDataCollection(dataCollection);
    }
  }, [prevDataCollection, questionnaireSection.name, dataCollection, isDataCollectionDirty]);

  useEffect(() => {
    if (!isLoadingTriggered) {
      return;
    }

    const unsubscribeDataCollection = subscribeDataCollection(
      organisationId,
      projectId,
      questionnaireSection.id,
      (fetchedDataCollection) => {
        setDataCollection(fetchedDataCollection ?? { id: questionnaireSection.id });
      }
    );

    return () => {
      unsubscribeDataCollection();
    };
  }, [isLoadingTriggered, organisationId, projectId, questionnaireSection.id, subscribeDataCollection]);

  const handleExpand = useCallback((event: React.SyntheticEvent, expanded: boolean) => {
    setIsLoadingTriggered(true);
    setIsExpanded(expanded);
  }, []);

  const dynamicQuestionnaireDetails = useMemo(() => {
    const dynamicQuestionnaireReference = questionnaireSection.dynamicQuestionnaireReference;

    if (dynamicQuestionnaireReference) {
      return {
        dynamicQuestionnaire: dynamicQuestionnaires?.find(
          (dq) => dq.id === dynamicQuestionnaireReference.dynamicQuestionnaireId
        ),
        maxOccurrence: dynamicQuestionnaireReference.maxOccurrence,
      };
    }
  }, [questionnaireSection.dynamicQuestionnaireReference, dynamicQuestionnaires]);

  const handleProjectSectionChange = async (projectSection: ProjectSection, submit?: boolean) => {
    setCurrentProjectSectionData(projectSection);
    setIsProjectSectionDirty(true);
    if (submit === true) {
      await submitProjectSectionDetails(projectSection);
    }
  };

  const submitProjectSectionDetails = useCallback(
    async (data: ProjectSection) => {
      try {
        setIsProjectSectionDirty(false);
        await saveProjectSection(organisationId, projectId, { ...data, id: questionnaireSection.id });
        enqueueSnackbar('Section data saved!', { variant: 'success' });
      } catch (error) {
        setIsProjectSectionDirty(true);
        enqueueSnackbar('Section data save failed! ' + error, { variant: 'error' });
      }
    },
    [saveProjectSection, organisationId, projectId, questionnaireSection.id]
  );

  const handleDataCollectionChange = useCallback(
    async (valueId: string, value: DataPointValue) => {
      const newCurrentData = { ...currentData };

      if (value === undefined) {
        if (Object.hasOwn(newCurrentData, valueId)) {
          delete newCurrentData[valueId];
        }
      } else {
        newCurrentData[valueId] = value;
      }

      setIsDataCollectionDirty(true);
      setCurrentData(newCurrentData);
    },
    [currentData]
  );

  const handleDeleteDataCollectionForDynamicItem = (dynamicItemName: string) => {
    const newCurrentData = { ...currentData };

    Object.keys(newCurrentData).forEach((key) => {
      if (key.startsWith(`${dynamicItemName}!`)) {
        delete newCurrentData[key];
      }
    });

    return newCurrentData;
  };

  const handleDataCollectionSubmission = useCallback(
    async (data?: DataCollectionData) => {
      setIsSaving(true);

      try {
        setIsDataCollectionDirty(false);
        await saveDataCollection(organisationId, projectId, {
          ...dataCollection,
          id: questionnaireSection.id,
          data: data ?? currentData,
        });
        enqueueSnackbar('Input data saved!', { variant: 'success' });
      } catch (error) {
        setIsDataCollectionDirty(true);
        enqueueSnackbar('Input data save failed! ' + error, { variant: 'error' });
      } finally {
        setIsSaving(false);
      }
    },
    [currentData, dataCollection, organisationId, projectId, saveDataCollection, questionnaireSection.id]
  );

  const handleSubmitProjectSection = async () => {
    const operationPromises = [];
    if (currentProjectSectionData && deepEqual(currentProjectSectionData, projectSection) === false) {
      operationPromises.push(submitProjectSectionDetails(currentProjectSectionData));
    }

    if (isDataCollectionDirty) {
      operationPromises.push(handleDataCollectionSubmission());
    }

    await Promise.all(operationPromises);
  };

  const managers = projectSection?.managers ?? [];

  const { usersEmailToNameMap } = useUsersEmailToNameMap();

  const getUserName = useCallback(
    (email?: string) => usersEmailToNameMap?.get(`${email}`) ?? email,
    [usersEmailToNameMap]
  );

  const handleExpandChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    // multiple panels opened - maybe in the future
    // setExpandedPanels((prev) => (isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)));
    setExpandedPanels(() => (isExpanded ? [panel] : []));
  };

  return (
    <Accordion onChange={handleExpand}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          spacing={3}
          sx={{ width: '100%', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}
        >
          <Typography sx={{ flex: 2 }}>{questionnaireSection.name}</Typography>

          <Box sx={{ flex: 1, display: 'inline-flex', flexWrap: 'wrap', gap: 1, width: '100%' }}>
            {managers.length > 0 && (
              <Tooltip title={getUserName(managers.at(0))}>
                <Chip
                  label={
                    <Typography variant="body2" noWrap={true} textOverflow={'ellipsis'}>
                      {getUserName(managers.at(0))}
                    </Typography>
                  }
                />
              </Tooltip>
            )}
            {managers.length > 1 && (
              <Tooltip
                title={
                  <Box sx={{ lineHeight: 2 }}>
                    {managers.slice(1).map((m) => (
                      <div key={m}>{getUserName(m)}</div>
                    ))}
                  </Box>
                }
              >
                <Chip label="..." />
              </Tooltip>
            )}
          </Box>

          <Typography sx={{ width: '80px' }}>
            {numberOfInputsProvided}/{totalNumberOfInputs}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {isExpanded && isLoadingTriggered && currentData && (
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              await handleSubmitProjectSection();
            }}
          >
            <Stack spacing={3}>
              <Card>
                <CardContent>
                  <Box display={'flex'}>
                    <Box sx={{ flex: 1 }}>
                      <QuestionnaireSectionDetailsMemo
                        project={project}
                        questionnaireSection={questionnaireSection}
                        projectSection={projectSection ?? { id: organisationId, managers: [] }}
                        handleProjectSectionChange={handleProjectSectionChange}
                        dynamicQuestionnaireDetails={dynamicQuestionnaireDetails}
                      />
                    </Box>
                    <Button
                      sx={{ alignSelf: 'flex-end', width: 'fit-content', marginLeft: 2 }}
                      type="submit"
                      variant="contained"
                      disabled={isSaving || (!isDataCollectionDirty && !isProjectSectionDirty)}
                    >
                      Save details
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              <Card>
                <Stack spacing={3}>
                  {isDebugMode && <h3> section id: {questionnaireSection.id} </h3>}
                  <Box sx={{ margin: 3, marginBottom: 0 }}>
                    <QuestionnaireSectionTableMemo
                      rows={questionnaireSection.rows}
                      dataCollection={currentData}
                      dataCollectionChangeHandler={handleDataCollectionChange}
                      globalVariables={sectionGlobalVariables}
                    />
                  </Box>
                  {(projectSection?.dynamicItems?.length ?? 0) > 0 &&
                    dynamicQuestionnaireDetails?.dynamicQuestionnaire && (
                      <>
                        <Divider />
                        <Card sx={{ marginX: 2 }}>
                          {projectSection!.dynamicItems!.map((dynamicItem) => {
                            const currExpanded = expandedPanels.includes(dynamicItem);

                            return (
                              <DynamicQuestionnaireAccordionMemo
                                key={dynamicItem}
                                name={dynamicItem}
                                dataCollection={currExpanded ? currentData : undefined}
                                dataCollectionChangeHandler={handleDataCollectionChange}
                                itemTypeName={dynamicQuestionnaireDetails.dynamicQuestionnaire!.name ?? 'Item'}
                                rows={currExpanded ? dynamicQuestionnaireDetails.dynamicQuestionnaire!.rows : []}
                                globalVariables={sectionGlobalVariables}
                                projectSection={projectSection ?? { id: organisationId, managers: [] }}
                                onDelete={async (project) => {
                                  const collectionAfterDelete = handleDeleteDataCollectionForDynamicItem(dynamicItem);
                                  await handleDataCollectionSubmission(collectionAfterDelete);
                                  await submitProjectSectionDetails(project);
                                }}
                                expanded={currExpanded}
                                onExpandChange={handleExpandChange(dynamicItem)}
                              />
                            );
                          })}
                        </Card>
                      </>
                    )}
                  <Divider />
                </Stack>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSaving || (!isDataCollectionDirty && !isProjectSectionDirty)}
                  >
                    Save details
                  </Button>
                </CardActions>
              </Card>
            </Stack>
          </form>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
